<ng-container *ngIf="request">
  <!-- Minimised -->
  <button
    type="button"
    class="sc-pill"
    *ngIf="minimized"
    [class.sc-shifted]="shifted"
    (click)="toggleMinimized()"
    aria-label="Expand SMS"
  >
    <i class="bi bi-chat-dots-fill"></i>
    <span class="sc-pill-name">{{ title }}</span>
    <span class="sc-badge" *ngIf="unread">{{ unread }}</span>
    <i class="bi bi-arrows-angle-expand"></i>
  </button>

  <section class="sc-panel" *ngIf="!minimized" [class.sc-shifted]="shifted" role="dialog" aria-label="SMS">
    <!-- Header -->
    <header class="sc-head">
      <div class="sc-avatar">
        <span *ngIf="sms.initials(request.contactName); else personIcon">{{ sms.initials(request.contactName) }}</span>
        <ng-template #personIcon><i class="bi bi-person-fill"></i></ng-template>
      </div>
      <div class="sc-head-text">
        <div class="sc-name" [title]="title">{{ title }}</div>
        <div class="sc-sub">
          <ng-container *ngIf="request.contactName">{{ sms.formatNumber(to ?? request.phoneNumber) }}</ng-container>
          <ng-container *ngIf="request.contactName && request.contactSubtitle"> · </ng-container>
          {{ request.contactSubtitle }}
        </div>
      </div>
      <div class="sc-head-actions">
        <button type="button" class="sc-icon" [class.sc-icon-on]="showSearch" (click)="toggleSearch()" title="Search" aria-label="Search">
          <i class="bi bi-search"></i>
        </button>
        <div class="sc-menu-wrap">
          <button type="button" class="sc-icon" (click)="showMenu = !showMenu" title="More" aria-label="More">
            <i class="bi bi-three-dots-vertical"></i>
          </button>
          <div class="sc-menu" *ngIf="showMenu">
            <button type="button" *ngIf="unread" (click)="markAsRead()"><i class="bi bi-check2-all"></i> Mark as read</button>
            <button type="button" *ngIf="!unread" (click)="markAsUnread()" [disabled]="!threadId"><i class="bi bi-envelope-exclamation"></i> Mark as unread</button>
            <button type="button" (click)="copy(to ?? request.phoneNumber); showMenu = false"><i class="bi bi-clipboard"></i> Copy number</button>
          </div>
        </div>
        <button type="button" class="sc-icon" (click)="toggleMinimized()" title="Minimise" aria-label="Minimise">
          <i class="bi bi-dash-lg"></i>
        </button>
        <button type="button" class="sc-icon" (click)="close()" title="Close" aria-label="Close">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </header>

    <div class="sc-from" *ngIf="senderNumber">
      <i class="bi bi-shield-check"></i>
      Texting from <strong>{{ sms.formatNumber(senderNumber) }}</strong>
    </div>

    <!-- Unread banner -->
    <div class="sc-unread-banner" *ngIf="unread && (holdUnread || thread?.markedUnreadByName)">
      <i class="bi bi-envelope-exclamation-fill"></i>
      <span>
        <strong>{{ unread }} unread</strong>
        <ng-container *ngIf="thread?.markedUnreadByName"> · marked by {{ thread?.markedUnreadByName }}<ng-container *ngIf="sms.toMillis(thread?.markedUnreadAt) as at">, {{ sms.formatDateTime(at) }}</ng-container></ng-container>
      </span>
      <button type="button" (click)="markAsRead()">Mark read</button>
    </div>

    <div class="sc-search" *ngIf="showSearch">
      <i class="bi bi-search"></i>
      <input type="text" [(ngModel)]="search" placeholder="Search this conversation…" autofocus />
      <span class="sc-search-count" *ngIf="search">{{ visibleMessages.length }}</span>
    </div>

    <!-- Conversation -->
    <div class="sc-body">
      <div #scroll class="sc-scroll" (scroll)="onScroll()">
        <div class="sc-state" *ngIf="loading">
          <span class="sc-spinner"></span> Loading conversation…
        </div>
        <div class="sc-state" *ngIf="lookupFailed">
          <i class="bi bi-exclamation-circle"></i> Couldn't load this conversation.
        </div>
        <div class="sc-state sc-state-empty" *ngIf="!loading && !lookupFailed && !messages.length">
          <i class="bi bi-chat-square-text"></i>
          <strong>No messages yet</strong>
          <span>Say hello - your first message starts the conversation.</span>
        </div>
        <div class="sc-state" *ngIf="search && messages.length && !visibleMessages.length">No messages match "{{ search }}".</div>

        <ng-container *ngFor="let m of visibleMessages; let i = index; trackBy: trackById">
          <div class="sc-day" *ngIf="isNewDay(i)"><span>{{ dayLabel(m) }}</span></div>
          <div class="sc-new-divider" *ngIf="unreadMessages[0]?.id === m.id && !search"><span>{{ unread }} new message{{ unread === 1 ? '' : 's' }}</span></div>

          <div class="sc-row" [class.sc-mine]="m.direction === 'outbound'" [class.sc-run-end]="isLastInRun(i)">
            <button
              type="button"
              class="sc-bubble"
              [class.sc-bubble-failed]="sms.statusTone(m) === 'failed'"
              [class.sc-bubble-selected]="selected?.id === m.id"
              [class.sc-bubble-unread]="isUnread(m)"
              (click)="select(m)"
              [title]="'View details'"
            >{{ m.text }}</button>

            <div class="sc-meta" *ngIf="isLastInRun(i) || sms.statusTone(m) === 'failed'">
              <span *ngIf="m.direction === 'outbound' && m.senderName">{{ m.senderName }} · </span>
              {{ timeOf(m) }}
              <ng-container *ngIf="m.direction === 'outbound'">
                <span class="sc-status" [attr.data-tone]="sms.statusTone(m)">
                  <i class="bi" [ngClass]="{
                    'bi-clock': sms.statusTone(m) === 'pending',
                    'bi-check2': sms.statusTone(m) === 'sent',
                    'bi-check2-all': sms.statusTone(m) === 'delivered',
                    'bi-exclamation-circle-fill': sms.statusTone(m) === 'failed'
                  }"></i>
                  {{ sms.statusLabel(m) }}
                </span>
              </ng-container>
            </div>
          </div>
        </ng-container>

        <div class="sc-read" *ngIf="!unread && thread?.readByName && messages.length">
          <i class="bi bi-eye"></i> Read by {{ thread?.readByName }}<ng-container *ngIf="sms.toMillis(thread?.readAt) as at"> · {{ sms.formatDateTime(at) }}</ng-container>
        </div>
      </div>

      <button type="button" class="sc-new-pill" *ngIf="newBelow" (click)="jumpToLatest()">
        <i class="bi bi-arrow-down"></i> New message
      </button>


      <!-- Message details -->
      <aside class="sc-details" *ngIf="selected as d">
        <div class="sc-details-head">
          <span>Message details</span>
          <button type="button" class="sc-icon sc-icon-dark" (click)="selected = null" aria-label="Close details"><i class="bi bi-x-lg"></i></button>
        </div>

        <div class="sc-details-status" [attr.data-tone]="sms.statusTone(d)">
          <i class="bi" [ngClass]="{
            'bi-arrow-down-left-circle-fill': sms.statusTone(d) === 'received',
            'bi-clock-fill': sms.statusTone(d) === 'pending',
            'bi-check-circle-fill': sms.statusTone(d) === 'sent' || sms.statusTone(d) === 'delivered',
            'bi-x-circle-fill': sms.statusTone(d) === 'failed'
          }"></i>
          {{ sms.statusLabel(d) }}
        </div>

        <!-- Progress: sent -> delivered, for outbound -->
        <ol class="sc-steps" *ngIf="d.direction === 'outbound'">
          <li class="sc-step-done">
            <span class="sc-step-dot"></span>
            <div><strong>Sent</strong><small>{{ sms.formatDateTime(sms.sentAtMillis(d)) }}</small></div>
          </li>
          <li [class.sc-step-done]="sms.statusTone(d) === 'delivered'" [class.sc-step-failed]="sms.statusTone(d) === 'failed'">
            <span class="sc-step-dot"></span>
            <div>
              <strong>{{ sms.statusTone(d) === 'failed' ? 'Not delivered' : 'Delivered' }}</strong>
              <small *ngIf="sms.statusTone(d) === 'delivered'">Confirmed by carrier</small>
              <small *ngIf="sms.statusTone(d) === 'sent' || sms.statusTone(d) === 'pending'">Waiting for confirmation</small>
              <small *ngIf="sms.statusTone(d) === 'failed'">The message didn't go through</small>
            </div>
          </li>
        </ol>

        <dl class="sc-dl">
          <dt>Direction</dt>
          <dd>{{ d.direction === 'outbound' ? 'Outgoing' : 'Incoming' }}</dd>

          <dt>From</dt>
          <dd>{{ d.direction === 'outbound' ? sms.formatNumber(senderNumber) : sms.formatNumber(to ?? request.phoneNumber) }}</dd>

          <dt>To</dt>
          <dd>{{ d.direction === 'outbound' ? sms.formatNumber(d.to?.[0] ?? to) : sms.formatNumber(senderNumber) }}</dd>

          <ng-container *ngIf="d.direction === 'outbound'">
            <dt>Sent by</dt>
            <dd>{{ d.senderName || '—' }}</dd>
          </ng-container>

          <ng-container *ngIf="d.direction === 'inbound'">
            <dt>Read by</dt>
            <dd>
              <ng-container *ngIf="isUnread(d); else readInfo"><span class="sc-unread-tag">Unread</span></ng-container>
              <ng-template #readInfo>
                {{ d.readByName || thread?.readByName || '—' }}
                <small class="sc-dd-sub" *ngIf="sms.toMillis(d.readAt) as at">{{ sms.formatDateTime(at) }}</small>
              </ng-template>
            </dd>
          </ng-container>

          <dt>{{ d.direction === 'outbound' ? 'Sent' : 'Received' }}</dt>
          <dd>{{ sms.formatDateTime(sms.sentAtMillis(d)) }}</dd>

          <ng-container *ngIf="sms.toMillis(d.statusUpdatedAt) as updated">
            <dt>Last update</dt>
            <dd>{{ sms.formatDateTime(updated) }}</dd>
          </ng-container>

          <dt>Length</dt>
          <dd>{{ d.text.length }} chars · {{ sms.segments(d.text, segmentSize) }} segment{{ sms.segments(d.text, segmentSize) === 1 ? '' : 's' }}</dd>

          <ng-container *ngIf="d.direction === 'outbound' && costPerSegment">
            <dt>Credits</dt>
            <dd>~{{ sms.segments(d.text, segmentSize) * costPerSegment }}</dd>
          </ng-container>

          <ng-container *ngIf="d.providerMessageId">
            <dt>Reference</dt>
            <dd class="sc-ref">
              <code>{{ d.providerMessageId }}</code>
              <button type="button" class="sc-icon sc-icon-dark" (click)="copy(d.providerMessageId)" title="Copy" aria-label="Copy reference"><i class="bi bi-clipboard"></i></button>
            </dd>
          </ng-container>
        </dl>

        <div class="sc-details-text">{{ d.text }}</div>

        <div class="sc-details-actions" *ngIf="d.direction === 'inbound'">
          <button type="button" class="sc-btn sc-btn-ghost" *ngIf="!isUnread(d)" (click)="markAsUnread(d)">
            <i class="bi bi-envelope-exclamation"></i> Mark unread from here
          </button>
          <button type="button" class="sc-btn sc-btn-ghost" *ngIf="isUnread(d)" (click)="markAsRead()">
            <i class="bi bi-check2-all"></i> Mark as read
          </button>
        </div>
      </aside>
    </div>

    <!-- Composer -->
    <footer class="sc-compose">
      <div class="sc-alert" *ngIf="!connections.sms && settingsReady">
        <i class="bi bi-exclamation-triangle-fill"></i>
        SMS is disconnected - reconnect it in Messaging Settings to send.
      </div>

      <div class="sc-compose-box">
        <textarea
          rows="1"
          [(ngModel)]="draft"
          (keydown)="onComposerKeydown($event)"
          [disabled]="!threadId || !connections.sms"
          placeholder="Write a message…"
          maxlength="1600"
        ></textarea>
        <button type="button" class="sc-send" [disabled]="!canSend" (click)="send()" aria-label="Send">
          <span class="sc-spinner sc-spinner-light" *ngIf="sending"></span>
          <i class="bi bi-send-fill" *ngIf="!sending"></i>
        </button>
      </div>

      <div class="sc-compose-foot">
        <select class="sc-template" *ngIf="templates.length" (change)="applyTemplate($any($event.target).value); $any($event.target).value = ''">
          <option value="">Templates…</option>
          <option *ngFor="let t of templates" [value]="t.id">{{ t.name }}</option>
        </select>
        <span class="sc-counter" [class.sc-counter-warn]="draftSegments > 1">
          {{ draft.length }} chars · {{ draftSegments }} segment{{ draftSegments === 1 ? '' : 's' }}<ng-container *ngIf="costPerSegment"> · ~{{ draftCost }} credits</ng-container>
        </span>
      </div>
    </footer>
  </section>
</ng-container>
